// Debug overlay for the Lucas-Kanade detector: paints every tracked corner
// over the camera preview (green = static, red = moving, with the sub-pixel
// displacement exaggerated into a trail) plus a HUD with the numbers the
// activation model sees. Enable with window.motionDebug.setOverlay(true).

export type LKDebugCorner = { x: number; y: number; dx: number; dy: number; moving: boolean };

/** Per-frame tracking data — corner coordinates live in the LK frame space. */
export type LKDebugInfo = {
  width: number;
  height: number;
  corners: LKDebugCorner[];
  tracked: number;
  total: number;
};

export type LKDebugFrame = LKDebugInfo & {
  movingFeatures: number;
  minMovingFeatures: number;
  suppressed: boolean;
  isMotion: boolean;
  processingMs: number;
};

// Displacements are sub-pixel, invisible at 1:1 — stretch trails so they read.
const TRAIL_SCALE = 10;
const STATIC_COLOR = "rgba(96, 220, 120, 0.8)";
const MOVING_COLOR = "rgba(255, 64, 64, 0.95)";
const LINE_HEIGHT = 16;

export default class DebugOverlay {
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;

  attach = () => {
    const container = document.querySelector("#camera-video-container");
    if (!container) {
      log.warn("Couldn't attach LK debug overlay, camera video container not found");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.id = "lk-motion-debug-overlay";
    canvas.style.position = "absolute";
    canvas.style.top = "0px";
    canvas.style.left = "0px";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    container.appendChild(canvas);
    this.canvas = canvas;
    this.context = canvas.getContext("2d");
  };

  detach = () => {
    this.canvas?.remove();
    this.canvas = null;
    this.context = null;
  };

  render = (frame: LKDebugFrame) => {
    const { canvas, context } = this;
    if (!canvas || !context) return;

    if (canvas.clientWidth && canvas.width !== canvas.clientWidth) canvas.width = canvas.clientWidth;
    if (canvas.clientHeight && canvas.height !== canvas.clientHeight) canvas.height = canvas.clientHeight;

    context.clearRect(0, 0, canvas.width, canvas.height);
    this.drawCorners(frame);
    this.drawHud(frame);
    if (frame.isMotion) {
      context.strokeStyle = MOVING_COLOR;
      context.lineWidth = 4;
      context.strokeRect(0, 0, canvas.width, canvas.height);
    }
  };

  private drawCorners = ({ width, height, corners }: LKDebugFrame) => {
    const { canvas, context } = this;
    if (!canvas || !context) return;

    const scaleX = canvas.width / width;
    const scaleY = canvas.height / height;

    for (const corner of corners) {
      // The preview is mirrored (scaleX(-1)), so mirror x to sit on the image.
      const x = (width - corner.x) * scaleX;
      const y = corner.y * scaleY;

      if (corner.moving) {
        context.strokeStyle = MOVING_COLOR;
        context.lineWidth = 1.5;
        context.beginPath();
        context.moveTo(x + corner.dx * TRAIL_SCALE * scaleX, y - corner.dy * TRAIL_SCALE * scaleY);
        context.lineTo(x, y);
        context.stroke();
        context.fillStyle = MOVING_COLOR;
        context.beginPath();
        context.arc(x, y, 4, 0, Math.PI * 2);
        context.fill();
      } else {
        context.strokeStyle = STATIC_COLOR;
        context.lineWidth = 1;
        context.beginPath();
        context.arc(x, y, 2.5, 0, Math.PI * 2);
        context.stroke();
      }
    }
  };

  private drawHud = ({ movingFeatures, minMovingFeatures, tracked, total, suppressed, isMotion, processingMs }: LKDebugFrame) => {
    const context = this.context;
    if (!context) return;

    const status = suppressed ? "suppressed" : isMotion ? "MOTION" : "rest";
    const lines = [
      `moving ${movingFeatures} / min ${minMovingFeatures}`,
      `tracked ${tracked} / ${total} corners`,
      `frame ${processingMs.toFixed(1)} ms`,
      status
    ];

    context.font = "12px monospace";
    context.fillStyle = "rgba(0, 0, 0, 0.6)";
    context.fillRect(8, 8, 180, 10 + lines.length * LINE_HEIGHT);
    lines.forEach((line, index) => {
      context.fillStyle = line === "MOTION" ? MOVING_COLOR : "white";
      context.fillText(line, 16, 24 + index * LINE_HEIGHT);
    });
  };
}
